import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import packageJson from '../../../../package.json';
import Layout from '../../../components/layout/Layout';
import TMSFull from '../../../assets/icons/TMS_Full';

const version = packageJson.version;

const changelog = [
  {
    version,
    date: '13/08/2026',
    entries: [
      { type: 'feature', text: 'Importación de plantilla CSV para tarifario de clientes' },
      { type: 'feature', text: 'Importación de plantilla CSV para costos de proveedores' },
      { type: 'feature', text: 'Carga, visualización y eliminación de archivos adjuntos en Clientes' },
    ],
  },
  {
    version: '1.0.10',
    date: '23/06/2026',
    entries: [
      { type: 'improvement', text: 'Pantalla de inicio reemplazada por dashboard de Power BI con indicadores clave del negocio' },
      { type: 'improvement', text: 'Mapa de rutas en tiempo real trasladado a Transacciones › Mapa de Rutas para mejor organización del menú' },
    ],
  },
  {
    version: '1.0.9',
    date: '28/05/2026',
    entries: [
      { type: 'feature', text: 'Campo Tipo de Ruta en costos de proveedor: combobox en modal, columna en listado y validación requerida' },
      { type: 'feature', text: 'Reporte de facturación: nuevas columnas Id Proveedor, Id Billing Cliente/Proveedor, Invoice ID, SO Nombre, SO Interno, OC Nombre, OC Interno' },
    ],
  },
  {
    version: '1.0.8',
    date: '07/05/2026',
    entries: [
      { type: 'feature', text: 'Módulo de Códigos de Facturación (CRUD completo con integración Odoo)' },
      { type: 'feature', text: 'Módulo de Clientes (CRUD completo con integración Odoo)' },
      { type: 'feature', text: 'Filtro de rango de fechas (Desde / Hasta) en columna Fecha de Invoices' },
      { type: 'feature', text: 'Filtro de rango de fechas (Desde / Hasta) en columna Fecha DTE de Invoices' },
      { type: 'feature', text: 'Exportar a Excel en módulo de Códigos de Facturación' },
      { type: 'improvement', text: 'Alineación de totales (Sub Total, IVA, Total) bajo columna Monto sin IVA en detalle de Invoice' },
      { type: 'fix', text: 'Corrección de campo combustible en rutas' },
      { type: 'fix', text: 'Corrección de mensaje de error en login por credenciales inválidas' },
      { type: 'fix', text: 'Campo Invoice ID ahora visible en Billing Tracking de clientes' },
    ],
  },
  {
    version: '1.0.5',
    date: '15/04/2026',
    entries: [
      { type: 'feature', text: 'Módulo de Invoices para clientes con envío a Odoo' },
      { type: 'feature', text: 'Envío y reprocesamiento de facturas a Odoo ERP' },
      { type: 'feature', text: 'Anulación de invoices con motivo de anulación' },
      { type: 'feature', text: 'Modal de historial de cambios (logs) en detalle de Invoice' },
      { type: 'feature', text: 'Integración con DTE: visualización de documentos tributales electrónicos' },
      { type: 'improvement', text: 'Validación de permisos granular por módulo y acción (ver, agregar, actualizar, eliminar)' },
    ],
  },
  {
    version: '1.0.4',
    date: '20/03/2025',
    entries: [
      { type: 'feature', text: 'Módulo de Facturación de Proveedores' },
      { type: 'feature', text: 'Integración con Odoo para órdenes de compra de proveedores' },
      { type: 'feature', text: 'Combobox de cliente/proveedor Odoo en formularios de facturación' },
      { type: 'improvement', text: 'Filtros de tabla con soporte para booleanos, fechas y texto con debounce' },
      { type: 'improvement', text: 'Exportar listados a Excel en módulos principales' },
    ],
  },
  {
    version: '1.0.3',
    date: '01/03/2025',
    entries: [
      { type: 'feature', text: 'Módulo de Combustible en Maestros' },
      { type: 'feature', text: 'Perfiles de usuario con permisos por pantalla y acción' },
      { type: 'feature', text: 'Módulo de Logs de auditoría en Configuración' },
      { type: 'improvement', text: 'Barra de navegación lateral con colapso automático al perder el foco' },
      { type: 'fix', text: 'Correcciones de rendimiento en carga de rutas con muchas órdenes' },
    ],
  },
];

const typeConfig = {
  feature: { label: 'Nueva función', color: 'secondary', icon: <NewReleasesOutlinedIcon sx={{ fontSize: 14 }} /> },
  improvement: { label: 'Mejora', color: 'primary', icon: <BuildOutlinedIcon sx={{ fontSize: 14 }} /> },
  fix: { label: 'Corrección', color: 'error', icon: <BugReportOutlinedIcon sx={{ fontSize: 14 }} /> },
};

const About = () => {
  return (
    <Layout propsToolbar={{ title: 'Acerca de', code: null, btnLabel: null }}>
      <Paper sx={{ p: 4, mb: 3, background: 'linear-gradient(135deg, #1a2a4a 0%, #2563a8 100%)' }}>
        <Stack spacing={2} alignItems="center">
          <Box sx={{ transform: 'scale(1.5)', my: 1 }}>
            <TMSFull />
          </Box>
          <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            Sistema de Gestión de Transporte
          </Typography>
          <Chip
            label={`Versión ${version}`}
            color="secondary"
            sx={{ fontWeight: 'bold', fontSize: '0.9rem', px: 1 }}
          />
        </Stack>
      </Paper>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
          Historial de mejoras
        </Typography>

        <Stack spacing={3}>
          {changelog.map((entry, idx) => (
            <Box key={entry.version}>
              {idx > 0 && <Divider sx={{ mb: 3 }} />}
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Chip
                  label={`v${entry.version}`}
                  color="secondary"
                  size="small"
                  sx={{ fontWeight: 'bold', minWidth: 70 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                  {entry.date}
                </Typography>
                {idx === 0 && (
                  <Chip label="Actual" color="success" size="small" variant="outlined" />
                )}
              </Stack>

              <List dense disablePadding>
                {entry.entries.map((item, i) => {
                  const cfg = typeConfig[item.type];
                  return (
                    <ListItem key={i} disablePadding sx={{ mb: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 28, mt: 0.3 }}>
                        <FiberManualRecordIcon
                          sx={{ fontSize: 8, color: `${cfg.color}.main`, mt: 0.5 }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={0.5}>
                            <Chip
                              label={cfg.label}
                              color={cfg.color}
                              size="small"
                              variant="outlined"
                              icon={cfg.icon}
                              sx={{ height: 20, fontSize: '0.68rem', '& .MuiChip-label': { px: 0.75 } }}
                            />
                            <Typography variant="body2">{item.text}</Typography>
                          </Stack>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ))}
        </Stack>
      </Paper>
    </Layout>
  );
};

export default About;
